import React from "react";
import { Container, Typography, Box, Paper, Divider } from "@mui/material";

function Terms(): JSX.Element {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Terms of Service
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Last updated: January 2025
        </Typography>
      </Box>
      
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing and using Local Poker, you accept and agree to be bound by the terms 
            and provision of this agreement. If you do not agree to abide by the above, please 
            do not use this service.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            2. Use License
          </Typography>
          <Typography variant="body1" paragraph>
            Permission is granted to temporarily use Local Poker for personal, non-commercial 
            transitory viewing only. This is the grant of a license, not a transfer of title, 
            and under this license you may not:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Modify or copy the materials
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Use the materials for any commercial purpose or for any public display
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Attempt to reverse engineer any software contained on the website
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Remove any copyright or other proprietary notations from the materials
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            3. Legal Compliance and Poker Game Hosting
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
            IMPORTANT: By using Local Poker to host or participate in poker games, you acknowledge 
            and agree that you are solely responsible for ensuring compliance with all federal, state, 
            and local laws in your jurisdiction regarding gambling, gaming, and poker activities.
          </Typography>
          <Typography variant="body1" paragraph>
            You expressly agree to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Verify and comply with all applicable laws in your jurisdiction before hosting any poker games
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Understand that laws regarding poker games vary significantly by location and may prohibit 
              certain types of games, stakes, or hosting arrangements
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Accept full legal responsibility for any games you host or participate in
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Indemnify and hold harmless Local Poker from any legal consequences arising from your 
              use of the platform
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Local Poker is a platform for connecting players and does not endorse, encourage, or 
            facilitate illegal gambling. The legality of poker games depends on your local jurisdiction, 
            and it is your responsibility to ensure compliance.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            4. User Conduct
          </Typography>
          <Typography variant="body1" paragraph>
            In addition to legal compliance, you agree to use Local Poker responsibly. 
            You are responsible for:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Providing accurate information when creating an account
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Maintaining the confidentiality of your account credentials
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
              Respecting other users and maintaining appropriate conduct
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            5. Privacy and Data
          </Typography>
          <Typography variant="body1" paragraph>
            Your privacy is important to us. We collect and use information in accordance with 
            our Privacy Policy. By using Local Poker, you consent to the collection and use of 
            information as outlined in our Privacy Policy.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            6. Disclaimer
          </Typography>
          <Typography variant="body1" paragraph>
            The materials on Local Poker are provided on an 'as is' basis. Local Poker makes 
            no warranties, expressed or implied, and hereby disclaims and negates all other 
            warranties including without limitation, implied warranties or conditions of 
            merchantability, fitness for a particular purpose, or non-infringement of 
            intellectual property or other violation of rights.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            7. Limitations
          </Typography>
          <Typography variant="body1" paragraph>
            In no event shall Local Poker or its suppliers be liable for any damages (including, 
            without limitation, damages for loss of data or profit, or due to business interruption) 
            arising out of the use or inability to use the materials on Local Poker, even if 
            Local Poker or a Local Poker authorized representative has been notified orally or 
            in writing of the possibility of such damage.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            8. Accuracy of Materials
          </Typography>
          <Typography variant="body1" paragraph>
            The materials appearing on Local Poker could include technical, typographical, or 
            photographic errors. Local Poker does not warrant that any of the materials on its 
            website are accurate, complete, or current.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            9. Modifications
          </Typography>
          <Typography variant="body1" paragraph>
            Local Poker may revise these terms of service at any time without notice. By using 
            this application, you are agreeing to be bound by the then current version of these 
            terms of service.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            10. Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about these Terms of Service, please contact us through 
            our contact page or email us at support@localpoker.info.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}

export default Terms;